import React from 'react';

const DEFAULT_ASSETS = '/static/landing/assets';

/**
 * Product walkthrough of the cross-board My Tasks home.
 *
 * The clip is decorative-but-informative marketing, so it must not cost the
 * page anything before it is on screen:
 *   - ``preload="none"`` plus a poster means nothing but the still is fetched
 *     until the section scrolls into view.
 *   - An IntersectionObserver starts playback only once visible, and pauses
 *     it again on the way out so a backgrounded tab is not decoding video.
 *   - Readers who ask for reduced motion get the poster and normal controls
 *     instead of anything moving on its own.
 */
export const WorkDemo = ({ assets = DEFAULT_ASSETS }) => {
  const ref = React.useRef(null);
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || reduced || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {
            /* autoplay refused (data saver, low power) — poster + controls stand in */
          });
        } else {
          el.pause();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  return (
    <figure className="work-demo">
      <video
        ref={ref}
        className="work-demo__video"
        src={`${assets}/my-work-demo.mp4`}
        poster={`${assets}/my-work-demo-poster.jpg`}
        muted
        loop
        playsInline
        preload="none"
        controls={reduced}
        aria-label="Walkthrough of the My Tasks home: work from every board in one list, ranked by what needs action"
      />
      <figcaption className="work-demo__caption">
        Every board you are on, in one list — ranked by what is blocking, overdue, or about to slip.
      </figcaption>
    </figure>
  );
};
